import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check, X, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { bindStreamToVideo, formatCameraError, requestCameraStream } from "../lib/cameraStream";

/**
 * Live-camera capture for KYC documents and selfies.
 *
 * File-uploaded photos can fail liveness checks (selfie) or lack the metadata
 * expected for ID verification. Capturing a fresh frame from getUserMedia
 * produces a real-time photo with consistent lighting and orientation.
 *
 * Flow:
 *   1. Mount -> request camera via navigator.mediaDevices.getUserMedia
 *   2. Show live <video> stream + CAPTURE button
 *   3. On capture: draw frame onto hidden <canvas>, convert to JPEG Blob
 *   4. Show preview with Retake / Use this
 *   5. onConfirm(blob, fileName) — parent uploads as usual
 *
 * Falls back to a file picker if the browser denies / lacks camera support.
 */
export default function SelfieCapture({
  open,
  onClose,
  onConfirm,
  title = "Live selfie",
  description = "Look straight at the camera, remove glasses, ensure even lighting. We'll capture a single frame for liveness verification.",
  facingMode = "user",
  filenamePrefix = "selfie",
  mirrorVideo = true,
  showFaceGuide = true,
  fallbackLabel = "Camera not working? Upload a recent selfie instead",
  testIdPrefix = "selfie",
  skipLabel,
  onSkip,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const streamRef = useRef(null);
  const startAbortRef = useRef(null);
  const [streamReady, setStreamReady] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const stopStream = () => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch (e) { /* noop */ }
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreamReady(false);
  };

  const attachStream = useCallback(async (video) => {
    const stream = streamRef.current;
    if (!video || !stream) return;
    setStreamReady(false);
    const ready = await bindStreamToVideo(video, stream);
    setStreamReady(ready);
    if (!ready) {
      setError("Camera preview didn't start. Allow camera access or use the file fallback below.");
    }
  }, []);

  const setVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current && open && !previewUrl) {
      attachStream(node);
    }
  }, [attachStream, open, previewUrl]);

  const startStream = async () => {
    setError(null);
    setPreviewUrl(null); setCapturedBlob(null);
    setStreamReady(false);
    startAbortRef.current?.abort();
    const abort = new AbortController();
    startAbortRef.current = abort;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported on this browser. Use the file fallback below.");
      const stream = await requestCameraStream({ facingMode, signal: abort.signal });
      if (abort.signal.aborted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        await attachStream(videoRef.current);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error("requestCameraStream failed", e);
        setError(formatCameraError(e));
      }
    } finally {
      if (startAbortRef.current === abort) startAbortRef.current = null;
    }
  };

  useEffect(() => {
    if (open) startStream(); else { stopStream(); setPreviewUrl(null); setCapturedBlob(null); setError(null); setCountdown(0); }
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facingMode]);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return setError("Capture failed — please retry.");
      setCapturedBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      stopStream();
    }, "image/jpeg", 0.92);
  };

  const startCountdown = () => {
    if (!streamReady) return;
    setCountdown(3);
    let n = 3;
    const tick = () => {
      n -= 1;
      if (n <= 0) { setCountdown(0); captureFrame(); return; }
      setCountdown(n);
      setTimeout(tick, 1000);
    };
    setTimeout(tick, 1000);
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setCapturedBlob(null);
    startStream();
  };

  const confirm = () => {
    if (!capturedBlob) return;
    const filename = `${filenamePrefix}_${Date.now()}.jpg`;
    onConfirm?.(capturedBlob, filename);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };

  const onFallbackPick = (e) => {
    const f = e.target.files?.[0];
    if (f) onConfirm?.(f, f.name || `${filenamePrefix}.jpg`);
    e.target.value = "";
  };

  const fallbackCapture = facingMode === "user" ? "user" : "environment";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-lg z-[60]" data-testid={`${testIdPrefix}-capture-modal`}>
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2">
            <Camera size={20} className="text-[var(--zc-accent)]" /> {title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[var(--zc-text-2)]">{description}</p>

        {error && (
          <div className="mt-3 p-3 rounded-lg flex items-start gap-2 text-xs" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", color: "var(--zc-warn,#f59e0b)" }}>
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        <div className="relative mt-3 rounded-xl overflow-hidden bg-black aspect-[4/3] grid place-items-center">
          {!previewUrl && (
            <video
              ref={setVideoRef}
              muted
              playsInline
              autoPlay
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-video`}
              style={mirrorVideo ? { transform: "scaleX(-1)" } : undefined}
            />
          )}
          {!previewUrl && !streamReady && !error && (
            <div className="absolute inset-0 grid place-items-center bg-black/80 text-white text-sm">
              Starting camera…
            </div>
          )}
          {previewUrl && (
            <img src={previewUrl} alt="Captured photo" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-preview`} />
          )}
          {countdown > 0 && (
            <div className="absolute inset-0 grid place-items-center bg-black/40">
              <div className="zc-stat text-7xl text-white" data-testid={`${testIdPrefix}-countdown`}>{countdown}</div>
            </div>
          )}
          {!previewUrl && streamReady && countdown === 0 && showFaceGuide && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="w-44 h-56 rounded-[50%] border-2 border-white/70" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)" }} />
            </div>
          )}
          {!previewUrl && streamReady && countdown === 0 && !showFaceGuide && (
            <div className="absolute inset-6 border-2 border-white/60 rounded-lg pointer-events-none" />
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="flex gap-2 mt-4">
          {!previewUrl ? (
            <>
              <button onClick={onClose} className="flex-1 h-11 zc-btn-ghost text-sm" data-testid={`${testIdPrefix}-cancel`}>
                Cancel
              </button>
              <button
                onClick={startCountdown}
                disabled={!streamReady || countdown > 0}
                className="flex-1 h-11 zc-btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid={`${testIdPrefix}-capture-btn`}
              >
                <Camera size={16} /> Capture
              </button>
            </>
          ) : (
            <>
              <button onClick={retake} className="flex-1 h-11 zc-btn-ghost text-sm flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-retake`}>
                <RotateCcw size={16} /> Retake
              </button>
              <button onClick={confirm} className="flex-1 h-11 zc-btn-primary text-sm flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-confirm`}>
                <Check size={16} /> Use this
              </button>
            </>
          )}
        </div>

        <div className="mt-3 text-center space-y-2">
          {skipLabel && onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="block w-full zc-hint underline hover:text-[var(--zc-text-2)]"
              data-testid={`${testIdPrefix}-skip-link`}
            >
              {skipLabel}
            </button>
          )}
          <button
            onClick={() => fallbackRef.current?.click()}
            className="zc-hint underline hover:text-[var(--zc-text-2)]"
            data-testid={`${testIdPrefix}-fallback-link`}
          >
            {fallbackLabel}
          </button>
          <input ref={fallbackRef} type="file" accept="image/*" capture={fallbackCapture} className="hidden" onChange={onFallbackPick} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
